import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Collapse, Drawer, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import tmAdminService from '../../services/tmService';
import { fmtTime, ok, showSecretOnce, toastErr } from './common';
import NodeOpsPanel from './NodeOpsPanel';

const { Text } = Typography;

const STATUS = { active: 'green', draining: 'orange', disabled: 'default' };

const Nodes = () => {
    const [rows, setRows] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // null | {} (new) | node
    const [saving, setSaving] = useState(false);
    const [checking, setChecking] = useState(null);
    const [opsId, setOpsId] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [n, g] = await Promise.all([tmAdminService.listNodes(), tmAdminService.listGroups()]);
            setRows(Array.isArray(n?.data) ? n.data : []);
            setGroups(Array.isArray(g?.data) ? g.data : []);
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openEdit = (n) => {
        setEditing(n || {});
        form.setFieldsValue(n ? {
            name: n.name, ip: n.ip, api_base: n.api_base, helo: n.helo, status: n.status, bind_ips: n.bind_ips || [],
            domain_limits: n.domain_limits ? JSON.stringify(n.domain_limits, null, 2) : '',
        } : { status: 'active', bind_ips: [], group_ids: groups.filter((g) => g.code === 'default').map((g) => g.id), api_base: 'https://', domain_limits: '' });
    };

    const save = async () => {
        const v = await form.validateFields();
        let limits = null;
        if (v.domain_limits && v.domain_limits.trim()) {
            try { limits = JSON.parse(v.domain_limits); } catch (_) { message.error('域名限速不是合法 JSON'); return; }
        }
        const data = { name: v.name, ip: v.ip, api_base: v.api_base, status: v.status, bind_ips: v.bind_ips || [], domain_limits: limits };
        if (v.helo) data.helo = v.helo;
        setSaving(true);
        try {
            if (editing?.id) {
                const r = await tmAdminService.updateNode(editing.id, data);
                if (ok(r)) { message.success('已保存'); setEditing(null); load(); }
            } else {
                const r = await tmAdminService.createNode({ ...data, group_ids: v.group_ids || [] });
                if (ok(r)) {
                    setEditing(null);
                    showSecretOnce('节点已创建', r.data.secret, '写入节点 /etc/yeap-tm/node.json');
                    load();
                }
            }
        } catch (e) {
            toastErr(e, '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const rotate = async (n) => {
        try {
            const r = await tmAdminService.rotateNodeSecret(n.id);
            if (ok(r)) showSecretOnce('新节点密钥', r.data.secret, '更新节点 /etc/yeap-tm/node.json 后重启节点服务');
        } catch (e) {
            toastErr(e, '操作失败');
        }
    };

    const remove = async (n) => {
        try {
            const r = await tmAdminService.deleteNode(n.id);
            if (ok(r)) { message.success('已删除'); load(); }
        } catch (e) {
            toastErr(e, '删除失败');
        }
    };

    const health = async (n) => {
        setChecking(n.id);
        try {
            const r = await tmAdminService.nodeHealth(n.id);
            if (ok(r)) {
                const d = r.data || {};
                message.success(`在线 · 队列 ${d.queue_depth ?? '-'} · 最老 ${d.oldest_age_s ?? '-'}s · ${d.version || ''}`, 5);
                load();
            }
        } catch (e) {
            toastErr(e, '节点不可达', true);
        } finally {
            setChecking(null);
        }
    };

    const groupName = (id) => groups.find((g) => g.id === id)?.name || `#${id}`;

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 60 },
        { title: '名称', dataIndex: 'name' },
        { title: 'IP', dataIndex: 'ip', render: (v, n) => <span>{v}{(n.bind_ips || []).length > 0 && <Text type="secondary" style={{ fontSize: 12 }}><br />{n.bind_ips.join(', ')}</Text>}</span> },
        { title: 'API', dataIndex: 'api_base', ellipsis: true },
        { title: 'HELO', dataIndex: 'helo', ellipsis: true },
        { title: '分组', dataIndex: 'groups', render: (v) => (v || []).map((g) => <Tag key={g}>{groupName(g)}</Tag>) },
        { title: '状态', dataIndex: 'status', width: 90, render: (v) => <Tag color={STATUS[v]}>{v}</Tag> },
        {
            title: '同步', key: 'sync', width: 170,
            render: (_, n) => (
                <Tooltip title={n.last_health ? JSON.stringify(n.last_health) : null}>
                    <Space direction="vertical" size={0}>
                        {n.healthy ? <Tag color="green">正常</Tag> : <Tag color="red">未同步</Tag>}
                        <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(n.last_sync_at)} v{n.last_sync_version ?? '-'}</Text>
                    </Space>
                </Tooltip>
            ),
        },
        {
            title: '', key: 'op', width: 270,
            render: (_, n) => (
                <Space size={0} wrap>
                    <Button type="link" size="small" loading={checking === n.id} onClick={() => health(n)}>检测</Button>
                    <Button type="link" size="small" onClick={() => openEdit(n)}>编辑</Button>
                    <Button type="link" size="small" onClick={() => setOpsId(n.id)}>运维</Button>
                    <Popconfirm title="旧密钥立即失效，节点需更新 node.json" onConfirm={() => rotate(n)}><Button type="link" size="small">换密钥</Button></Popconfirm>
                    <Popconfirm title="删除节点？" okButtonProps={{ danger: true }} onConfirm={() => remove(n)}><Button type="link" size="small" danger>删除</Button></Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Card title="发信节点" extra={<Space><Button icon={<ReloadOutlined />} onClick={load} /><Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>添加节点</Button></Space>}>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns} pagination={false} scroll={{ x: 1100 }} />
            <Drawer open={!!opsId} onClose={() => setOpsId(null)} width={720} title="节点运维" destroyOnClose>
                {opsId ? <NodeOpsPanel nodeId={opsId} onChanged={load} /> : null}
            </Drawer>
            <Modal open={!!editing} title={editing?.id ? `编辑节点 #${editing.id}` : '添加节点'} onOk={save} confirmLoading={saving} onCancel={() => setEditing(null)} destroyOnClose width={560}>
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input placeholder="o1" /></Form.Item>
                    <Form.Item name="ip" label="IP" rules={[{ required: true }]}><Input placeholder="195.26.249.252" /></Form.Item>
                    <Form.Item name="api_base" label="节点 API" rules={[{ required: true }]}><Input placeholder="https://o1.tm.yeapcloud.net:8443" /></Form.Item>
                    <Form.Item name="status" label="状态">
                        <Select options={[{ value: 'active', label: 'active' }, { value: 'draining', label: 'draining（不再分配新邮件）' }, { value: 'disabled', label: 'disabled' }]} />
                    </Form.Item>
                    {!editing?.id && (
                        <Form.Item name="group_ids" label="加入分组">
                            <Select mode="multiple" options={groups.map((g) => ({ value: g.id, label: `${g.name} (${g.code})` }))} />
                        </Form.Item>
                    )}
                    <Collapse ghost size="small" items={[{
                        key: 'adv', label: '高级',
                        children: (
                            <>
                                <Form.Item name="helo" label="HELO"><Input placeholder="o1.tm.yeapcloud.net" /></Form.Item>
                                <Form.Item name="bind_ips" label="出站 IP"><Select mode="tags" tokenSeparators={[',', ' ']} /></Form.Item>
                                <Form.Item name="domain_limits" label="收件域限速 (JSON)" style={{ marginBottom: 0 }}>
                                    <Input.TextArea rows={4} placeholder='{"gmail.com": {"conns": 5, "per_min": 60}}' style={{ fontFamily: 'monospace' }} />
                                </Form.Item>
                            </>
                        ),
                    }]} />
                </Form>
            </Modal>
        </Card>
    );
};

export default Nodes;
